import { useEffect, useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../../data";
import { useAction } from "../../../components/ui";
import { ErrorState, Loading } from "../../../components/States";
import { t, ta, istCreatorKonto } from "../../../lib/sprache";
import { TextField } from "./parts";

/**
 * Rechnungen automatisch an die Buchhaltung (0163, Bauplan 1.2): Beleg-Adresse des Buchhaltungsprogramms; jede festgeschriebene
 * Rechnung geht genau einmal mit PDF dorthin. CreatorOffice ersetzt keine Buchhaltung, es liefert die Belege an.
 */
interface Weiterleitung {
  accounting_forward_email: string | null; accounting_forward_outgoing: boolean; accounting_forward_incoming: boolean; accounting_forward_since: string | null;
}
const MAIL = /^[^@\s"<>,;]+@[^@\s"<>,;]+\.[^@\s"<>,;]+$/;

export function BuchhaltungWeiterleitung({ orgId }: { orgId: string }) {
  const q = useQuery({
    queryKey: ["org_settings", orgId, "buchhaltung"],
    queryFn: async () => (await api.select<Weiterleitung>("org_settings", {
      columns: "accounting_forward_email, accounting_forward_outgoing, accounting_forward_incoming, accounting_forward_since", filters: [["org_id", "eq", orgId]],
    }))[0] ?? null,
  });
  // Kopie jeder Rechnungsmail (0080, Einstellungen · Vorlagen): an dieselbe Adresse kämen Ausgangsrechnungen doppelt an
  const kopie = useQuery({
    queryKey: ["org_mail_settings", orgId, "invoice_copy_to"],
    queryFn: async () => (await api.select<{ invoice_copy_to: string | null }>("org_mail_settings", {
      columns: "invoice_copy_to", filters: [["org_id", "eq", orgId]],
    }))[0]?.invoice_copy_to ?? null,
  });
  const { busy, run } = useAction();
  const [mail, setMail] = useState("");
  const [aus, setAus] = useState(true);
  const [ein, setEin] = useState(true);
  const [fehler, setFehler] = useState<string | undefined>();
  useEffect(() => {
    if (!q.data) return;
    setMail(q.data.accounting_forward_email ?? "");
    setAus(q.data.accounting_forward_outgoing);
    setEin(q.data.accounting_forward_incoming);
  }, [q.data]);
  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (q.isPending) return <Loading />;
  const aktiv = !!q.data?.accounting_forward_email;

  function speichern(e: FormEvent) {
    e.preventDefault();
    const m = mail.trim().toLowerCase();
    if (m && !MAIL.test(m)) { setFehler(t("Bitte eine gültige E-Mail-Adresse eintragen.", "Please enter a valid email address.")); return; }
    setFehler(undefined);
    void run("buchhaltung", async () => {
      const rows = await api.update("org_settings", [["org_id", "eq", orgId]], { accounting_forward_email: m || null, accounting_forward_outgoing: aus, accounting_forward_incoming: ein });
      if (!rows.length) throw new Error(t("Die Einstellung ließ sich nicht speichern.", "The setting could not be saved."));
      await q.refetch();
    }, m ? t("Gespeichert. Neue Rechnungen gehen ab jetzt automatisch an die Buchhaltung.", "Saved. New invoices now go to your accounting automatically.")
         : t("Weiterleitung ausgeschaltet.", "Forwarding turned off."));
  }

  return (
    <section className="panel stack" aria-labelledby="buchhaltung-title">
      <h2 id="buchhaltung-title">{t("Automatisch an die Buchhaltung", "Automatically to your accounting")}</h2>
      <p>{t("Trag die Beleg-Adresse deines Buchhaltungsprogramms ein, etwa von Lexware Office (lexoffice), sevDesk, DATEV Unternehmen online oder Buchhaltungsbutler. Jede festgeschriebene Rechnung geht dann genau einmal mit PDF dorthin, ohne Export von Hand. Rechnungen eines Creators direkt an eine Marke gehören nicht in eure Bücher und gehen nicht mit.",
        "Enter the document email address of your accounting software, for example Lexware Office (lexoffice), sevDesk, DATEV Unternehmen online or Buchhaltungsbutler. Every finalised invoice is then sent there exactly once with its PDF, without manual export. Invoices from a creator directly to a brand do not belong in your books and are not forwarded.")}</p>
      <form className="stack" onSubmit={speichern} noValidate aria-busy={busy === "buchhaltung"}>
        <TextField label={t("Beleg-Adresse", "Document email address")} type="email" value={mail} onChange={setMail} error={fehler} autoComplete="off"
          placeholder="belege@beispiel-buchhaltung.de" hint={t("Leer lassen schaltet die Weiterleitung aus. Gilt nur für Rechnungen ab dem Speichern, nichts rückwirkend.", "Leave empty to turn forwarding off. Applies only to invoices from the moment you save, nothing retroactively.")} />
        <label className="row tk-check"><input type="checkbox" checked={aus} onChange={(e) => setAus(e.target.checked)} /><span>{ta("Ausgangsrechnungen (an Marken und an Creator, mit Gutschriften und Stornos)", "Outgoing invoices (to brands and to creators, including credit notes and cancellations)", "Ausgangsrechnungen an Marken (mit Stornos)", "Outgoing invoices to brands (including cancellations)")}</span></label>
        {!istCreatorKonto() && <label className="row tk-check"><input type="checkbox" checked={ein} onChange={(e) => setEin(e.target.checked)} /><span>{t("Eingangsrechnungen der Creator an die Agentur (nach eurer Freigabe)", "Incoming invoices from creators to the agency (after your approval)")}</span></label>}
        {aus && !!mail.trim() && kopie.data?.trim().toLowerCase() === mail.trim().toLowerCase() && (
          <p className="notice warning" role="status">{t("An diese Adresse geht schon die Kopie jeder Rechnungs- und Mahnmail (Einstellungen · Mailversand). Ausgangsrechnungen kämen dort doppelt an: hier die Ausgangsrechnungen abwählen oder dort die Kopie leeren.",
            "This address already receives a copy of every invoice and dunning email (Settings · Email sending). Outgoing invoices would arrive twice: untick outgoing invoices here or clear the copy there.")}</p>
        )}
        <div className="row">
          <button type="submit" className="btn primary" disabled={!!busy}>{busy === "buchhaltung" ? t("Wird gespeichert …", "Saving …") : t("Speichern", "Save")}</button>
          <button type="button" className="btn" disabled={!aktiv || !!busy}
            onClick={() => void run("buchhaltung-test", () => api.rpc("org_accounting_forward_test", { p_org: orgId }), t("Testmail ist unterwegs.", "Test email is on its way."))}>
            {t("Testmail senden", "Send test email")}
          </button>
        </div>
      </form>
      {aktiv && <small>{t(`Aktiv: Belege gehen an ${q.data!.accounting_forward_email}.`, `Active: documents go to ${q.data!.accounting_forward_email}.`)}</small>}
    </section>
  );
}
